import { ROLES, TREASURY } from "@coup/shared";
import { motion } from "motion/react";
import { Avatar } from "../../components/Avatar";
import { CardFace } from "../../components/Card";
import { roleName, type Names } from "./names";
import type { Anim } from "./useDisplayedGame";
import styles from "./Table.module.css";

type Point = { x: number; y: number };

const TREASURY_POS = { x: 305, y: 340 };
const EXILED_Y = 600;

/** Centre of a role's slot in the Exiled row. */
const exiledSlot = (role: string): Point => ({
  x: 430 - 128 + ROLES.indexOf(role as never) * 54 + 22,
  y: EXILED_Y,
});

interface Props {
  anims: Anim[];
  names: Names;
  seatPos: (id: string) => Point;
}

export function AnimLayer({ anims, names, seatPos }: Props) {
  const at = (id: string) => (id === TREASURY ? TREASURY_POS : seatPos(id));
  return (
    <>
      {anims.map((a) => {
        switch (a.t) {
          case "coins": {
            const from = at(a.from);
            const to = at(a.to);
            return Array.from({ length: Math.min(a.amount, 3) }, (_, i) => (
              <motion.div
                key={`${a.key}-${i}`}
                className={styles.flyingCoin}
                initial={{ x: from.x - 11, y: from.y - 11, opacity: 0 }}
                animate={{ x: to.x - 11, y: to.y - 11, opacity: [0, 1, 1, 0.9] }}
                transition={{ duration: 0.45, delay: i * 0.05, ease: "easeInOut" }}
              />
            ));
          }
          case "reveal": {
            const p = seatPos(a.playerId);
            return (
              <motion.div
                key={a.key}
                className={styles.flyingCard}
                style={{ left: p.x - 36, top: p.y - 130 }}
                initial={{ rotateY: 90, scale: 0.9 }}
                animate={{ rotateY: 0, scale: 1.15 }}
                transition={{ duration: 0.35 }}
              >
                <CardFace role={a.role} size="sm" />
              </motion.div>
            );
          }
          case "exile": {
            const from = seatPos(a.playerId);
            const to = exiledSlot(a.role);
            return (
              <motion.div
                key={a.key}
                className={styles.flyingCard}
                initial={{ x: from.x - 22, y: from.y - 31, rotate: -12 }}
                animate={{ x: to.x - 22, y: to.y - 31, rotate: 0 }}
                transition={{ duration: 0.55, ease: "easeInOut" }}
              >
                <CardFace role={a.role} size="mini" />
              </motion.div>
            );
          }
          case "bullshit":
            return (
              <div key={a.key} className={styles.stampWrap}>
                <div className={styles.stamp}>
                  <div className={styles.stampWord}>Bullshit!</div>
                  <div className={styles.stampLine}>
                    <Avatar avatar={names.avatar(a.by)} size={34} ring={2} />
                    {names.name(a.by)} challenges {names.name(a.accused)}&apos;s {roleName(a.role)}
                    <Avatar avatar={names.avatar(a.accused)} size={34} ring={2} />
                  </div>
                </div>
              </div>
            );
          case "block":
            return (
              <div key={a.key} className={styles.stampWrap}>
                <div className={styles.blockCallout}>
                  <Avatar avatar={names.avatar(a.by)} size={34} ring={2} />
                  {names.name(a.by)} blocks with {roleName(a.role)}
                </div>
              </div>
            );
        }
      })}
    </>
  );
}
